import { makeStyles } from "@mui/styles";
import { AutocompleteMenuPortalContext } from "components/Form/components/Autocomplete/AutocompleteMenuPortalContext";
import { RelationshipRecordForm } from "components/Form/RelationshipRecordForm";
import { centeredPopoverRootStyle, Popover } from "components/Popover/Popover";
import { FeRecordType, FeRelationshipType } from "config/config";
import { getRecordConfig } from "config/resourceUtils";
import "./Prompt.scss";
import "./RecordPrompt.scss";

// Default dialog width (the `md` step of the modals-dialogs width scale).
const DEFAULT_PROMPT_WIDTH = "600px";
// Wide (`lg`) dialog width for forms that embed the Advanced-Search filter
// builder (recordFilterBuilder). A single filter row is intrinsically ~825px
// (field 240 + operator 120 + value 420 + margins) and does not wrap
// (Filter.scss `flex-wrap: nowrap`), so at the 600px default the op/value side
// of the row is clipped off-screen by `.recordPrompt__content { overflow-x:
// hidden }` and the user cannot see the filter they defined. Per
// ui-design-principle-modals-dialogs (size the dialog to its content; dropdowns
// are not portaled so an inner horizontal scroller would clip the inline
// react-select menus), the correct fix is a wider paper, not an inner scroller.
// 1040px leaves ~992px of inner content (minus 48px padding) — enough for a
// multi-condition row (~930px) with slack — and is still bounded below the
// viewport by the maxWidth cap.
const FILTER_BUILDER_PROMPT_WIDTH = "1040px";

// True when the given relationship record type's edit form embeds the
// recordFilterBuilder control (currently plugin_action; detected from config so
// any future filter-bearing form widens automatically rather than needing a
// hard-coded resource-name list).
export const formHasRecordFilterBuilder = (resourceName: FeRelationshipType): boolean => {
    const resourceConfig: any = getRecordConfig(resourceName);
    return (
        Array.isArray(resourceConfig?.form) &&
        resourceConfig.form.some((field: any) => field?.component === "recordFilterBuilder")
    );
};

export interface RecordPromptData {
    resourceName: FeRelationshipType;
    recordId: string;
    isReadOnly: boolean;
    isDisabled: boolean;
    parentResourceType?: FeRecordType;
    parentResourceId?: string;
}

export interface RecordPromptProps {
    data: RecordPromptData;
    isOpen: boolean;
    onConfirm?: () => void;
    onCancel?: () => void;
    rootStyle?: any;
    isScrollable?: boolean;
}

export const RecordPrompt = ({
    data,
    isOpen,
    rootStyle = centeredPopoverRootStyle,
    onConfirm,
    onCancel,
    isScrollable,
}: RecordPromptProps) => {
    const paperStyle = {
        width: formHasRecordFilterBuilder(data.resourceName)
            ? FILTER_BUILDER_PROMPT_WIDTH
            : DEFAULT_PROMPT_WIDTH,
        minWidth: "300px",
        // Bound the width so the dialog is not wider than needed and never forces
        // a horizontal scrollbar on smaller viewports.
        maxWidth: "calc(100vw - 64px)",
        overflow: isScrollable ? "scroll" : "visible",
    };

    const useStyles = makeStyles(() => ({
        rootStyle,
        paperStyle,
    }));
    const classes = useStyles();

    return (
        <Popover
            isOpen={isOpen}
            anchorReference="none"
            classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
            rootClassName="recordPrompt__popover"
            contentClassName="recordPrompt__content"
        >
            <AutocompleteMenuPortalContext.Provider value={true}>
                <RelationshipRecordForm
                    data={data}
                    onCancel={onCancel}
                    onConfirm={onConfirm}
                    isOpen={isOpen}
                    // Keep the user's unsaved edits when a background /ui push
                    // reinitializes the form's initialValues (the store rebuilds
                    // record objects with fresh identities on every push, so any
                    // edit-related-record popover would otherwise silently lose
                    // in-progress edits). role_permission is the documented
                    // exception: it relies on keepDirtyOnReinitialize=false so
                    // that fetched values overwrite the schema-seeded defaults
                    // (see RelationshipRecordForm). Confine that behaviour to
                    // role_permission; every other record type keeps its edits.
                    keepDirtyOnReinitialize={data.resourceName !== "role_permission"}
                />
            </AutocompleteMenuPortalContext.Provider>
        </Popover>
    );
};
